<template>
  <div
    ref="menuContainer"
    class="relative z-50">
    <UButton
      color="primary"
      icon="i-lucide-menu"
      size="lg"
      variant="solid"
      @click="toggleMenu" />

    <div
      v-if="isMenuOpen"
      class="absolute top-11 left-0 w-56 flex flex-col space-y-1 bg-primary border border-primary rounded-xl shadow-lg p-3">
      <NuxtLink
        class="menuLink"
        active-class="menuLinkActive"
        to="/home/maintable"
        @click="closeMenu">
        Home page
      </NuxtLink>

      <NuxtLink
        :class="['menuLink', { 'disabled-link': presencesStore.hasNoColaborators }]"
        :to="presencesStore.hasNoColaborators ? undefined : '/home/dailyrecord'"
        active-class="menuLinkActive"
        @click="closeMenu">
        Daily Record
      </NuxtLink>

      <NuxtLink
        :class="['menuLink', { 'disabled-link': !presencesStore.hasEditablePresences }]"
        :to="presencesStore.hasEditablePresences ? '/home/editDailyRecord' : undefined"
        active-class="menuLinkActive"
        @click="closeMenu">
        Edit Daily Record
      </NuxtLink>

      <NuxtLink
        class="menuLink"
        active-class="menuLinkActive"
        to="/home/usercolaborators"
        @click="closeMenu">
        User Colaborators
      </NuxtLink>

      <NuxtLink
        :class="['menuLink', { 'disabled-link': !userHasActivitiesRights }]"
        active-class="menuLinkActive"
        to="/home/usersActivities"
        @click="closeMenu">
        Users Activities
      </NuxtLink>

      <NuxtLink
        class="menuLink"
        active-class="menuLinkActive"
        to="/home/historic"
        @click="closeMenu">
        Get Historic
      </NuxtLink>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { useGlobalStore } from '~/stores/GlobalStore'
  import { useLoginStore } from '~/stores/LoginStore'
  import { usePresencesStore } from '~/stores/PresencesStore'

  const globalStore = useGlobalStore()
  const loginStore = useLoginStore()
  const presencesStore = usePresencesStore()

  const isMenuOpen: Ref<boolean> = ref(false)
  const menuContainer: Ref<HTMLElement | null> = ref(null)

  const userHasActivitiesRights = computed(function (): boolean {
    return globalStore.pagesRights?.mainPage?.usersActivities?.includes(loginStore.userEmail ?? '') ?? false
  })

  function toggleMenu(): void {
    isMenuOpen.value = !isMenuOpen.value
  }

  function closeMenu(): void {
    isMenuOpen.value = false
  }

  function handleClickOutside(event: MouseEvent): void {
    if (!isMenuOpen.value) return

    const target = event.target
    if (target instanceof Node && menuContainer.value && !menuContainer.value.contains(target)) {
      closeMenu()
    }
  }

  onMounted(function () {
    document.addEventListener('click', handleClickOutside)
  })

  onBeforeUnmount(function () {
    document.removeEventListener('click', handleClickOutside)
  })
</script>

<style scoped>
  @reference "tailwindcss";

  .menuLink {
    @apply text-orange-50 font-medium px-3 py-1.5 rounded-lg cursor-pointer transition-colors hover:bg-white/15 hover:text-white;
  }

  .menuLinkActive {
    @apply bg-white text-orange-600 font-semibold shadow-sm hover:bg-white hover:text-orange-600;
  }

  .disabled-link {
    @apply text-orange-200/60 cursor-not-allowed pointer-events-none;
  }
</style>
